/**
 * The status design language — one place that decides what a state *looks* like.
 *
 * Tones are chip class names from `src/styles/base.css`; components never pick a
 * colour themselves. Contract truth first, presentation second:
 * `DeviceSummary.online` + `.stale` are the only freshness signals the façade
 * gives us, and `stale` is `!online || !last_inform_at` server-side.
 */

import type {
  ActivationStatusValue,
  AlarmSeverity,
  ConfigSectionStatus,
  DeviceSummary,
  JobStatus,
  MaintenanceState,
  ProblemCode,
  IncidentState,
} from '../api/types';

export type Tone =
  | 'online'
  | 'offline'
  | 'stale'
  | 'unknown'
  | 'critical'
  | 'major'
  | 'minor'
  | 'warning'
  | 'cleared'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'neutral'
  | 'accent'
  | 'danger';

export const CHIP_CLASS: Record<Tone, string> = {
  online: 'chip chip-online',
  offline: 'chip chip-offline',
  stale: 'chip chip-stale',
  unknown: 'chip chip-unknown',
  critical: 'chip chip-critical',
  major: 'chip chip-major',
  minor: 'chip chip-minor',
  warning: 'chip chip-warning',
  cleared: 'chip chip-cleared',
  queued: 'chip chip-queued',
  running: 'chip chip-running',
  completed: 'chip chip-completed',
  failed: 'chip chip-failed',
  neutral: 'chip chip-neutral',
  accent: 'chip chip-accent',
  danger: 'chip chip-danger',
};

/** Dot-only colour (legend, row markers). */
export const DOT_CLASS: Record<Tone, string> = {
  online: 'chip-dot bg-status-online',
  offline: 'chip-dot bg-status-offline',
  stale: 'chip-dot bg-status-stale',
  unknown: 'chip-dot bg-status-unknown',
  critical: 'chip-dot bg-severity-critical',
  major: 'chip-dot bg-severity-major',
  minor: 'chip-dot bg-severity-minor',
  warning: 'chip-dot bg-severity-warning',
  cleared: 'chip-dot bg-severity-cleared',
  queued: 'chip-dot bg-job-queued',
  running: 'chip-dot bg-job-running',
  completed: 'chip-dot bg-job-completed',
  failed: 'chip-dot bg-job-failed',
  neutral: 'chip-dot bg-ink-faint',
  accent: 'chip-dot bg-accent',
  danger: 'chip-dot bg-danger',
};

export type DeviceState = 'online' | 'stale' | 'offline';

/** Freshness of one device, derived exactly from the two contract booleans. */
export function deviceState(
  device: Pick<DeviceSummary, 'online' | 'stale' | 'last_inform_at'>,
): DeviceState {
  if (!device.online) return 'offline';
  if (device.stale || device.last_inform_at === null) return 'stale';
  return 'online';
}

export function deviceTone(
  device: Pick<DeviceSummary, 'online' | 'stale' | 'last_inform_at'>,
): Tone {
  return deviceState(device);
}

export const DEVICE_STATE_LABELS: Record<DeviceState, string> = {
  online: 'آنلاین',
  offline: 'آفلاین',
  stale: 'کهنه',
};

export function jobTone(status: string): Tone {
  const known: readonly JobStatus[] = ['queued', 'running', 'completed', 'failed'];
  return known.includes(status as JobStatus) ? (status as Tone) : 'unknown';
}

export function activationTone(status: ActivationStatusValue): Tone {
  switch (status) {
    case 'provisioned':
      return 'completed';
    case 'bound':
      return 'cleared';
    case 'informed':
      return 'running';
    case 'await_inform':
      return 'queued';
    case 'failed':
      return 'failed';
    default:
      return 'unknown';
  }
}

/** Severity strings come from OLT traps (`onu.los.data.severity`) — free text. */
export function severityTone(severity: string | null | undefined): Tone {
  switch ((severity ?? '').toLowerCase()) {
    case 'critical':
      return 'critical';
    case 'major':
      return 'major';
    case 'minor':
      return 'minor';
    case 'warning':
    case 'warn':
      return 'warning';
    case 'cleared':
    case 'ok':
      return 'cleared';
    default:
      return 'unknown';
  }
}

/**
 * The alarm feed's own severity — the closed contract enum
 * (`ALARM_SEVERITIES`), NOT the free-text trap vocabulary above. `info` is a
 * hygiene row, so it stays neutral instead of borrowing a colour.
 */
/**
 * ALM-05 — the row's state, and nothing derived from the clock: `active`
 * is a plan in force, `cancelled` is a plan a person ended. A window whose
 * end has passed is STILL `active` here, exactly as the server stores it.
 */
export function maintenanceStateTone(state: MaintenanceState): Tone {
  return state === 'active' ? 'running' : 'neutral';
}

export function incidentStateTone(state: IncidentState): Tone {
  return state === 'open' ? 'running' : 'neutral';
}

export function alarmSeverityTone(severity: AlarmSeverity): Tone {
  switch (severity) {
    case 'critical':
      return 'critical';
    case 'warning':
      return 'warning';
    case 'info':
      return 'neutral';
    default:
      return 'unknown';
  }
}

/** ONU registration on the OLT: `registered` is live, `unregistered` is not. */
export function onuStatusTone(status: string | null | undefined): Tone {
  switch (status) {
    case 'registered':
      return 'online';
    case 'unregistered':
      return 'offline';
    default:
      return 'unknown';
  }
}

/** ONU authorize state on the OLT — `authorized` is the intended end state. */
export function onuAuthTone(authState: string | null | undefined): Tone {
  switch (authState) {
    case 'authorized':
      return 'completed';
    case 'unauthorized':
      return 'stale';
    default:
      return 'unknown';
  }
}

/**
 * Which tone a contract error deserves: destructive/terminal errors are danger,
 * retryable ones are warning, informational ones stay neutral. This is what
 * makes a `rate-limited` banner read differently from a `binding-conflict`.
 */
export const PROBLEM_TONE: Record<ProblemCode, Tone> = {
  'acs-unreachable': 'critical',
  'device-not-found': 'danger',
  'device-offline': 'major',
  'binding-conflict': 'major',
  'action-unsupported': 'minor',
  'policy-conflict': 'major',
  'job-timeout': 'major',
  'job-not-found': 'danger',
  'contract-version-unsupported': 'critical',
  'stale-snapshot': 'minor',
  'reference-invalid': 'minor',
  'scope-insufficient': 'major',
  'rate-limited': 'warning',
  'idempotency-conflict': 'warning',
  // U-03-a: same family as the line above — «act on this before you retry» — and deliberately not
  // `danger`: nothing broke, a sibling request is still running.
  'idempotency-in-progress': 'warning',
  'signature-invalid': 'critical',
  'olt-not-found': 'danger',
  'adapter-unreachable': 'critical',
  // U-05/C1d (AUD-05/G-OLT-13): both are 503 and both are «this deployment's adapter link
  // is not usable right now». `adapter-unconfigured` is critical for the same reason
  // `auth-unconfigured` is — nothing about the operator's request was wrong, and the
  // deployment itself is missing a piece, so it reads as a setup problem. The second one is
  // a write that did not happen, and the adapter's contract says a retry is safe, so it is
  // warning («act now: retry») rather than critical («this deployment is broken»).
  'adapter-unconfigured': 'critical',
  'adapter-state-unavailable': 'warning',
  // C9: the adapter answered and its answer was unusable — nothing about the deployment
  // is broken and nothing about the operator's request was wrong, so it is not
  // `critical` («this deployment is broken») and not `danger` (nothing to look up). The
  // operator acts now: the answer has to be corrected and the request re-sent under a
  // fresh key, so `warning`.
  'adapter-answer-invalid': 'warning',
  // G-PRV-05: the NBI answered and refused the document — the operator acts now
  // (correct the document, resend under the handed-back key), so `warning`.
  'acs-refused': 'warning',
  'firmware-not-found': 'danger',
  'campaign-not-found': 'danger',
  'alarm-not-found': 'danger',
  'maintenance-not-found': 'danger',
  'config-point-not-found': 'danger',
  // N5: a lookup miss is danger; the two refusals that say «the row is fine, the
  // request is aimed wrong» are minor — the same two rules the block above states.
  'incident-not-found': 'danger',
  'incident-already-closed': 'minor',
  'alarm-already-in-incident': 'minor',
  // N6: a channel lookup miss is danger; the two body-shaped refusals are minor
  // (the same rule the incident block above states).
  'notification-channel-not-found': 'danger',
  'channel-kind-unsupported': 'minor',
  'notification-url-invalid': 'minor',
  'report-not-found': 'danger',
  // The same three rules, applied to the circuit-M/T codes: a lookup miss is danger (nothing to act
  // on), a refusal that says «this door is not for that» is minor (the device is fine, the request
  // was aimed wrong), and a missing typed confirmation is warning (retry it, the words are in the body).
  'capability-unproven': 'minor',
  'operation-not-found': 'danger',
  'confirmation-required': 'warning',
  'manifest-not-found': 'danger',
  'not-an-action': 'minor',
  'job-not-cancellable': 'minor',
  // E2: nothing broke on the device and nothing was written — the operator has a file to fix and a
  // free key to retry with. That is warning (act now, on the upload), not danger (no lookup missed).
  'checksum-mismatch': 'warning',
  // Circuit H: an unrecorded store and an ahead store are both 'fix the deployment before you
  // retry' states, and they are not the same one. `--baseline` is one command (warning); reading
  // newer data with older code is a wrong deploy (critical). A missing backup directory is major:
  // the recovery material exists somewhere the API cannot see. `*-not-found` stays danger by the
  // table's own rule — a lookup miss means there is nothing here to act on.
  // H4-b: a missed delivery record is danger (nothing to act on); a row that is not a dead letter
  // is minor — the event is fine, the request was aimed at the wrong state.
  'event-not-found': 'danger',
  'event-not-redrivable': 'minor',
  'migration-unrecorded': 'warning',
  'migration-ahead': 'critical',
  // DA-38. Both single-flight refusals are the same band as `migration-unrecorded`: nothing
  // ran, nothing is wrong with the build, and the operator's next step is to re-run the door.
  'migration-busy': 'warning',
  'migration-lost-race': 'warning',
  'backup-not-found': 'danger',
  'backup-dir-unavailable': 'major',
  // U-17: the miss is the not-found family (`danger`); the second lift is a conflict with the
  // record's own state, which is `major` like `binding-conflict` — not a broken request.
  'legal-hold-not-found': 'danger',
  'legal-hold-already-lifted': 'major',
  // R1: `auth-unconfigured` is the deployment being unset, not the operator being refused — nobody
  // can log in until an owner is seeded, so it is critical and the console shows a setup problem
  // rather than a login failure. A missing user is the table's own lookup-miss rule (danger), a taken
  // username and the last-owner guard are both «act on this before you retry» (warning) rather than
  // anything broken: the deployment is fine and the request was aimed at a state it cannot reach.
  'auth-unconfigured': 'critical',
  'user-not-found': 'danger',
  'user-exists': 'warning',
  'last-owner': 'warning',
  // U-06 (`REQ-PROFILE`). None of the three is a lookup miss, so none is danger. `profile-invalid`
  // and `profile-stale-version` are both «act on this before you retry» — the first with a report
  // that lists every line to fix (the `checksum-mismatch` family), the second with a re-read and a
  // diff (the `idempotency-conflict` family). `profile-release-blocked` is major: the complaints are
  // only this deployment's release rules, and the remedy is not in this request at all — the claim
  // (a `mock` integration in production, or no secret reference) has to change before a release can
  // go through, and an operator who reads that as a warning signs off against it. The report still
  // answers `valid: false` there, because `valid` counts the release rules too.
  'profile-invalid': 'warning',
  'profile-stale-version': 'warning',
  'profile-release-blocked': 'major',
  // U-06-d: `profile-version-not-found` is the lookup-miss family this table already has a rule for
  // (`backup-not-found`, `user-not-found`, `device-not-found`): nothing is broken and nothing is
  // refused on purpose — the row the operator named is simply not in this store's window. `danger`
  // here, not `major`: an operator who reads it as «release blocked» would edit the deployment's
  // claim, when the whole answer is «pick a version that is still listed».
  'profile-version-not-found': 'danger',
  // N1/N2 (G-ALM-03/06): the alarm lifecycle's refusals. `alarm-already-cleared` and the three
  // «a closed record moves no more» codes are `minor`: nothing broke and nothing was lost — the
  // record is settled, and the operator's next step is to read it, exactly like
  // `job-not-cancellable`. `actor-not-human` is `major`, the same band as `scope-insufficient`:
  // the door is closed to this caller, and the remedy is a different credential, not a retry.
  'alarm-already-cleared': 'minor',
  'alarm-not-escalatable': 'minor',
  'alarm-not-assignable': 'minor',
  'alarm-not-ackable': 'minor',
  'actor-not-human': 'major',
  // S1 (G-SUB-01/03). `subscriber-disabled` is the deployment being unset rather than the caller
  // being refused — the same band as `auth-unconfigured`: nothing about the operator's request was
  // wrong, and the remedy is a setting and a restart. The two `*-not-found` codes are the lookup-miss
  // rule (danger: nothing to act on), and `service-profile-conflict` is «the name is taken — pick
  // another», which is warning (act now, then retry) and not danger: nothing was lost.
  'subscriber-disabled': 'critical',
  'subscriber-not-found': 'danger',
  'service-profile-not-found': 'danger',
  'service-profile-conflict': 'warning',
  // S4 (G-SUB-05). `subscriber-state-conflict` is the same band as `alarm-already-cleared`
  // and `job-not-cancellable`: nothing broke, nothing was lost, and the record is settled
  // — the operator's next step is to READ the row, which is exactly what the row's own
  // `status` field is for. It is not danger (a lookup miss: nothing to act on) and not
  // warning (a write that did not happen and may be retried as it was).
  'subscriber-state-conflict': 'minor',
};

/** Optical power band (plan §B2 thresholds; defaults are engineering guesses). */
export function opticalTone(rxDbm: number | null | undefined): Tone {
  if (typeof rxDbm !== 'number' || !Number.isFinite(rxDbm)) return 'unknown';
  if (rxDbm <= -27) return 'critical';
  if (rxDbm <= -25) return 'major';
  if (rxDbm <= -22) return 'warning';
  return 'cleared';
}

/* ------------------------------------------------------------------ */
/* Campaigns — what a wave's state looks like                           */
/* ------------------------------------------------------------------ */

/**
 * `Campaign.status` → tone.
 *
 * `pending` is queued, not broken: since E4 the create only schedules the ladder, so
 * the first wave is owed by the campaign worker. `halted` is the auto-halt firing —
 * the one campaign state an operator must go and look at.
 */
export function campaignStatusTone(status: string): Tone {
  switch (status) {
    case 'pending':
      return 'queued';
    case 'running':
      return 'running';
    case 'completed':
      return 'completed';
    case 'halted':
      return 'major';
    case 'rolling_back':
      return 'warning';
    default:
      return 'unknown';
  }
}

/**
 * `CampaignNextAction.action` → tone.
 *
 * The two dispatching verdicts are accent because they are the ladder moving;
 * `wait_jobs` is queued because the read-back has not landed yet; a closed window, an
 * exhausted ladder and a settled record are all neutral — nothing is wrong, nothing is
 * coming without a decision.
 */
export function campaignActionTone(action: string): Tone {
  switch (action) {
    case 'advance':
    case 'continue_wave':
      return 'accent';
    case 'wait_jobs':
      return 'queued';
    case 'wait_window':
    case 'idle':
    case 'settled':
      return 'neutral';
    default:
      return 'unknown';
  }
}

/**
 * `CampaignWindow.status` → tone. `passed` is major rather than neutral: a window that
 * already closed will not open by waiting, so the campaign needs a human, not a pass.
 */
export function campaignWindowTone(status: string): Tone {
  switch (status) {
    case 'waiting':
      return 'neutral';
    case 'open':
      return 'cleared';
    case 'passed':
      return 'major';
    default:
      return 'unknown';
  }
}

/**
 * `ConfigSectionReport.status` → tone (W2). Only non-reserved tones: a bundle section is not an alarm
 * severity, and `will-change` is not an emergency — it is the state the operator is about to approve.
 */
export function configStatusTone(status: ConfigSectionStatus): Tone {
  switch (status) {
    case 'unchanged':
      return 'neutral';
    case 'will-change':
      return 'accent';
    case 'invalid':
      return 'failed';
    case 'absent':
      return 'unknown';
    case 'not-permitted':
      return 'danger';
    default:
      return 'unknown';
  }
}
